/* @moldura padrao */
/**
 * form-completo.tsx — Lançar e AJUSTAR despesa no sistema. Rotas
 * `/despesas/lancar` (nova) e `/despesas/:id` (edição).
 *
 * A porta de entrada é a lista de despesas, no desktop: a Patrícia com a
 * notinha que o instalador entregou, ou abrindo uma despesa já lançada para
 * corrigir. É trabalho sentado, muitas vezes em série — e aí a regra se inverte
 * em relação ao formulário de campo:
 *
 *   - NADA recolhido. Descrição e OS aparecem sempre: quem ajusta veio conferir
 *     o que está lá, e um campo escondido é um campo que ela não sabe que existe.
 *   - Duas colunas. A tela é larga; empilhar tudo obrigaria a rolar por algo que
 *     cabe de uma vez.
 *   - Label FLUTUANTE, dentro da caixa (padrão do projeto): a linha fica mais
 *     baixa, cabe mais na tela e o rótulo continua legível depois de preenchido.
 *
 * A REGRA não mora aqui: está em `useDespesaForm`. Esta tela só desenha. O
 * arranjo de campo da mesma coisa é `form-campo.tsx`.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CustomCampoNumeroMoeda } from '@/components/CustomCamposNumero';
import { CustomCampoData } from '@/components/CustomCamposData';
import { rotuloOrigem, varianteOrigem } from './shared';
import { useDespesaForm } from './useDespesaForm';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';
import { AbaTiny } from './aba-tiny';
import { BlocoParcelamento } from './bloco-parcelamento';
import { Modal } from 'react-bootstrap';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/auth/auth-context';

function badge(v: string): string {
  const mapa: Record<string, string> = {
    success: 'text-bg-success',
    warning: 'text-bg-warning',
    destructive: 'text-bg-danger',
    secondary: 'text-bg-secondary',
  };
  return mapa[v] ?? 'text-bg-secondary';
}

export function DespesaFormCompletoPage() {
  const { id } = useParams();
  const f = useDespesaForm(id, { desktop: true });
  // Duas abas, um formulário cada (decisão de 13/08, retomada em 25/09): a
  // despesa como o app a entende, e o que vai para o Tiny. Só na EDIÇÃO — o
  // Tiny precisa da despesa gravada, com loja.
  const [aba, setAba] = useState<'despesa' | 'tiny'>('despesa');
  const [apagar, setApagar] = useState(false);
  const { pode } = useAuth();

  return (
    <CustomMolduraPagina
      titulo={f.editando ? 'Editar despesa' : 'Nova despesa'}
      descricao={
        f.editando && f.origem
          ? `Origem ${rotuloOrigem(f.origem as never)}${
              f.lancadoPor ? ` • lançado por ${f.lancadoPor}` : ''
            }`
          : 'Lançamento do financeiro'
      }
      caminho={[{ rotulo: 'Despesas', para: f.destinoAoSair }]}
      /* ⛔ NÃO CRAVAR O DESTINO AQUI. Eu tinha posto
         `voltar={{ titulo: 'Despesas', caminho: f.destinoAoSair }}`, e ele
         pegou o defeito na hora, em 31/08/2026: *"eu estava em VIAGENS, cliquei
         em lançar a despesa, e o chevron aponta para a raiz de Despesas — eu
         estava em Viagens"*. Estava certo. `destinoAoSair` responde a outra
         pergunta — "para onde ir DEPOIS DE SALVAR" —, e usá-la como caminho de
         volta ressuscita exatamente o `voltarPara` cravado que esta frente
         nasceu para matar.

         Sem `voltar`, a moldura usa a TRILHA: volta para a tela anterior de
         verdade, seja ela Viagens, a lista de Despesas ou a Inbox. E para subir
         de nível existe o breadcrumb, logo abaixo do título. */
      acoes={
        f.editando && f.origem ? (
          <span className={`badge ${badge(varianteOrigem(f.origem as never))}`}>
            {rotuloOrigem(f.origem as never)}
          </span>
        ) : undefined
      }
    >

      {f.editando && id && (
        <ul className="nav nav-tabs adminuiux-tabs mb-3 mb-lg-4">
          {([
            ['despesa', 'Despesa', 'bi-receipt'],
            ['tiny', 'Tiny', 'bi-send'],
          ] as const).map(([k, rotulo, icone]) => (
            <li className="nav-item" key={k}>
              <button
                type="button"
                className={`nav-link ${aba === k ? 'active' : ''}`}
                onClick={() => setAba(k)}
              >
                <i className={`bi ${icone} me-1`}></i>
                {rotulo}
              </button>
            </li>
          ))}
        </ul>
      )}

      {f.carregando ? (
        <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
          <span className="spinner-border spinner-border-sm" /> Carregando…
        </div>
      ) : aba === 'tiny' && id ? (
        <AbaTiny despesaId={id} lojaId={f.form.loja_id} aoMudarEnvio={f.setNoTiny} />
      ) : (
        <>
        <form onSubmit={f.salvar}>
          <div className="row g-3">
            <div className="col-lg-8">
              {/* Dados da despesa */}
              <div className="card adminuiux-card mb-3">
                <div className="card-header bg-transparent">
                  <h6 className="mb-0">Dados da despesa</h6>
                </div>
                <div className="card-body">
                  {f.noTiny && (
                    <p className="small text-secondary">
                      <i className="bi bi-lock me-1"></i>
                      Já está no Tiny: valor, data, loja, categoria e pagamento não se
                      alteram mais aqui. Histórico, descrição, viagem, OS e comprovante,
                      sim.
                    </p>
                  )}
                  <div className="row g-3">
                    <fieldset disabled={f.noTiny} className="col-sm-4">
                      <CustomCampoNumeroMoeda
                        label="Valor"
                        id="despesa-valor"
                        valor={f.form.valor}
                        onChange={(v) => f.setCampo('valor', v)}
                      />
                    </fieldset>
                    <fieldset disabled={f.noTiny} className="col-sm-4">
                      {/* Data Rápida: digitar 1108 vira 11/08. */}
                      <CustomCampoData
                        label="Data"
                        id="despesa-data"
                        valor={f.form.data}
                        onChange={(iso) => f.setCampo('data', iso)}
                      />
                    </fieldset>
                    <fieldset disabled={f.noTiny} className="col-sm-4">
                      <div className="form-floating">
                        <select
                          id="despesa-forma"
                          className="form-select"
                          value={f.form.forma_pgto}
                          onChange={(e) => f.escolherForma(e.target.value)}
                        >
                          <option value="">—</option>
                          {f.formas.map((fp) => (
                            <option key={fp.codigo} value={fp.codigo}>
                              {fp.nome}
                            </option>
                          ))}
                        </select>
                        <label htmlFor="despesa-forma">Forma de pagamento</label>
                      </div>
                      {f.formaEscolhida?.ressarcivel && (
                        <div className="form-text text-warning">
                          Gera restituição a quem pagou.
                        </div>
                      )}
                    </fieldset>

                    {/* Cartão de qual loja? — só aparece p/ 'Cartão da loja'. */}
                    {f.pedeLoja && (
                      <fieldset disabled={f.noTiny} className="col-sm-4">
                        <div className="form-floating">
                          <select
                            id="despesa-loja-cartao"
                            className="form-select"
                            value={f.form.loja_cartao_id}
                            onChange={(e) =>
                              f.setCampo('loja_cartao_id', e.target.value)
                            }
                          >
                            <option value="">—</option>
                            {f.lojas.map((l) => (
                              <option key={l.id} value={String(l.id)}>
                                {f.rotuloLoja(l)}
                              </option>
                            ))}
                          </select>
                          <label htmlFor="despesa-loja-cartao">
                            Cartão de qual loja
                          </label>
                        </div>
                      </fieldset>
                    )}

                    {/* Categoria com a linha inteira: é a escolha que exige ler,
                        e o critério dela aparece logo abaixo. */}
                    <fieldset disabled={f.noTiny} className="col-12">
                      <div className="form-floating">
                        <select
                          id="despesa-categoria"
                          className="form-select"
                          value={f.form.categoria_id}
                          onChange={(e) =>
                            f.setCampo('categoria_id', e.target.value)
                          }
                        >
                          <option value="">—</option>
                          {f.categorias.map((c) => (
                            <option key={c.id} value={String(c.id)}>
                              {c.nome}
                            </option>
                          ))}
                        </select>
                        <label htmlFor="despesa-categoria">Categoria</label>
                      </div>
                      {f.categoriaEscolhida?.criterio && (
                        <div className="form-text">
                          {f.categoriaEscolhida.criterio}
                        </div>
                      )}
                    </fieldset>

                    <div className="col-12">
                      <div className="form-floating">
                        <input
                          id="despesa-historico"
                          className="form-control"
                          maxLength={120}
                          placeholder=" "
                          value={f.form.historico}
                          onChange={(e) =>
                            f.setCampo('historico', e.target.value)
                          }
                        />
                        <label htmlFor="despesa-historico">Histórico</label>
                      </div>
                      <div className="form-text">
                        A linha curta que aparece na lista — ex.: "Pedágio - Rio".
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-floating">
                        <textarea
                          id="despesa-descricao"
                          className="form-control"
                          style={{ height: 100 }}
                          placeholder=" "
                          value={f.form.descricao}
                          onChange={(e) =>
                            f.setCampo('descricao', e.target.value)
                          }
                        />
                        <label htmlFor="despesa-descricao">
                          Descrição (quando precisa explicar)
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Vínculos — todos visíveis: quem ajusta veio conferir. */}
              <div className="card adminuiux-card">
                <div className="card-header bg-transparent">
                  <h6 className="mb-0">Vínculos</h6>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-sm-6">
                      <div className="form-floating">
                        <select
                          id="despesa-viagem"
                          className="form-select"
                          value={f.form.viagem_id}
                          onChange={(e) => f.escolherViagem(e.target.value)}
                        >
                          <option value="">—</option>
                          {f.viagens.map((v) => (
                            <option key={v.id} value={String(v.id)}>
                              {v.label}
                            </option>
                          ))}
                        </select>
                        <label htmlFor="despesa-viagem">Viagem</label>
                      </div>
                    </div>
                    <fieldset disabled={f.noTiny} className="col-sm-6">
                      <div className="form-floating">
                        <select
                          id="despesa-loja"
                          className="form-select"
                          value={f.form.loja_id}
                          onChange={(e) => f.setCampo('loja_id', e.target.value)}
                        >
                          <option value="">—</option>
                          {f.lojas.map((l) => (
                            <option key={l.id} value={String(l.id)}>
                              {f.rotuloLoja(l)}
                            </option>
                          ))}
                        </select>
                        <label htmlFor="despesa-loja">Loja</label>
                      </div>
                    </fieldset>
                    <div className="col-12">
                      <div className="form-floating">
                        <select
                          id="despesa-os"
                          className="form-select"
                          value={f.form.os_id}
                          onChange={(e) => f.setCampo('os_id', e.target.value)}
                        >
                          <option value="">—</option>
                          {f.osLista.map((o) => (
                            <option key={o.id} value={String(o.id)}>
                              {o.codigo}
                              {o.cliente_nome ? ` — ${o.cliente_nome}` : ''}
                            </option>
                          ))}
                        </select>
                        <label htmlFor="despesa-os">Ordem de serviço</label>
                      </div>
                      <div className="form-text">
                        Com uma viagem escolhida, lista as OS dela; sem viagem, as
                        OS disponíveis.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Coluna lateral: comprovante + ações. Aqui ele fica na lateral
                mesmo — na tela larga, olhar a nota enquanto se digita é o gesto
                natural de quem lança a partir do papel. */}
            <div className="col-lg-4">
              <div className="card adminuiux-card mb-3">
                <div className="card-header bg-transparent">
                  <h6 className="mb-0">Comprovante</h6>
                  {/* Dele, 25/09: o comprovante nunca sobe para o Tiny. */}
                  <p className="text-secondary small mb-0">Fica só aqui no app — não vai para o Tiny.</p>
                </div>
                <div className="card-body">
                  {f.form.comprovante ? (
                    <div className="mb-3 text-center">
                      <a
                        href={`/uploads/${f.form.comprovante}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img
                          src={`/uploads/${f.form.comprovante}`}
                          alt="Comprovante"
                          className="img-fluid rounded border"
                          style={{ maxHeight: 280 }}
                        />
                      </a>
                    </div>
                  ) : (
                    <div className="text-secondary small mb-3">
                      Nenhum comprovante anexado.
                    </div>
                  )}
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-theme"
                      onClick={() => f.fileRef.current?.click()}
                      disabled={f.enviandoFoto}
                    >
                      {f.enviandoFoto ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Enviando…
                        </>
                      ) : (
                        <>
                          <i className="bi bi-paperclip me-1"></i>
                          {f.form.comprovante ? 'Trocar' : 'Anexar'}
                        </>
                      )}
                    </button>
                    {f.form.comprovante && (
                      <button
                        type="button"
                        className="btn btn-link text-danger"
                        onClick={f.removerComprovante}
                      >
                        Remover
                      </button>
                    )}
                  </div>
                  <input
                    ref={f.fileRef}
                    type="file"
                    accept="image/*"
                    className="d-none"
                    onChange={f.onArquivo}
                  />
                </div>
              </div>

              <div className="d-grid gap-2">
                <button
                  type="submit"
                  className="btn btn-theme"
                  disabled={f.salvando || f.enviandoFoto}
                >
                  {f.salvando && (
                    <span className="spinner-border spinner-border-sm me-2" />
                  )}
                  {f.editando ? 'Salvar alterações' : 'Lançar despesa'}
                </button>
                <button
                  type="button"
                  className="btn btn-link text-secondary"
                  onClick={f.sair}
                >
                  Cancelar
                </button>
              </div>

              {/* ARQUIVAR E APAGAR moram AQUI desde 25/09, não mais na lista:
                  "ninguém apaga uma despesa lançada por ali" (dele). Arquivar
                  tira da frente; apagar some de vez — e só se não foi ao Tiny. */}
              {f.editando && id && (
                <div className="card adminuiux-card mt-3">
                  <div className="card-body d-flex flex-column gap-2">
                    {f.arquivadaEm && (
                      <p className="small text-secondary mb-1">
                        <i className="bi bi-archive me-1"></i>Arquivada — fora da lista de trabalho.
                      </p>
                    )}
                    {pode('despesas.lancamento.arquivar') && (
                      <button
                        type="button"
                        className="btn btn-outline-theme"
                        onClick={async () => {
                          const vai = !f.arquivadaEm;
                          try {
                            const d = await api.post<{ arquivada_em: string | null }>(
                              `/despesas/${id}/${vai ? 'arquivar' : 'desarquivar'}`,
                            );
                            f.setArquivadaEm(d.arquivada_em ?? null);
                            toast.success(vai ? 'Despesa arquivada.' : 'Despesa de volta à lista.');
                          } catch (e) {
                            toast.error(e instanceof ApiError ? e.message : 'Não foi possível arquivar.');
                          }
                        }}
                      >
                        <i className={`bi ${f.arquivadaEm ? 'bi-box-arrow-up' : 'bi-archive'} me-1`}></i>
                        {f.arquivadaEm ? 'Desarquivar' : 'Arquivar'}
                      </button>
                    )}
                    {pode('despesas.lancamento.excluir') && !f.noTiny && (
                      <button
                        type="button"
                        className="btn btn-link text-danger"
                        onClick={() => setApagar(true)}
                      >
                        <i className="bi bi-trash me-1"></i>Apagar despesa
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
        {/* PARCELAS (migration 117): na aba Despesa, fora do <form> — parcelar
            é coisa do app, e o Enter da condição não pode salvar a despesa. */}
        {f.editando && id && (
          <div className="row g-3 mt-0">
            <div className="col-lg-8">
              <BlocoParcelamento despesaId={id} bloqueado={f.noTiny} />
            </div>
          </div>
        )}
        </>
      )}
      <Modal className="casaa-modal" show={apagar} onHide={() => setApagar(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Apagar despesa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">A despesa some de vez — da lista, da viagem e do custo.</p>
          <p className="mb-0 text-secondary small">
            Se ela só precisa sair da frente, use <strong>Arquivar</strong>: não apaga nada.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-link" onClick={() => setApagar(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={async () => {
              try {
                await api.delete(`/despesas/${id}`);
                toast.success('Despesa apagada.');
                setApagar(false);
                f.sair();
              } catch (e) {
                toast.error(e instanceof ApiError ? e.message : 'Não foi possível apagar.');
              }
            }}
          >
            Apagar
          </button>
        </Modal.Footer>
      </Modal>
    </CustomMolduraPagina>
  );
}
